import WrappedTable, { ColumnProps } from '@/components/ui/WrappedTable';
import { useMemo, useState } from 'react';
import {
  useMediaQuery,
  Typography,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, type Atom } from 'jotai';
import { atomWithImmer } from 'jotai-immer';

import useTheme from '@/theme/useTheme';
import {
  query,
  EventStatus,
  history,
  type AirdropProjectDTOS,
} from '@/apis/airdrops';
import { useRouter } from 'next/router';
import useAirdropList from './useAirdropList';
import useParticipatedList from './useParticipatedList';

export const airdropsDetail = atomWithImmer<AirdropProjectDTOS>(null);

const AllAirdrops = () => {
  const { theme } = useTheme();
  const router = useRouter();
  const ongoing = useAirdropList(EventStatus.ONGOING);
  const upcoming = useAirdropList(EventStatus.UPCOMING);
  const ended = useAirdropList(EventStatus.ENDED);
  const [_, setAirdropsDetail] = useAtom(airdropsDetail);
  const getRowStyle = (row: any) => {
    return {
      '&:hover': {
        bgcolor: '#000',
        cursor: 'pointer',
      },
    };
  };
  const renderAirdropTable = (res, title) => (
    <Stack>
      <Typography variant="h4" my={3}>
        {title} ({res.data?.airdropProjectDTOS?.length || 0})
      </Typography>
      <WrappedTable
        getRowStyle={title == 'Ongoing' && getRowStyle}
        onRowClick={(item) => {
          if (title == 'Ongoing') {
            setAirdropsDetail(item);
            router.push({ pathname: `/airdrops/${item.name}` });
          }
        }}
        loading={res.isLoading}
        sxs={{
          containerSX: {
            width: '100%',
            whiteSpace: 'nowrap',
          },
        }}
        columns={columns}
        data={res.data?.airdropProjectDTOS || []}
        anchoredButtonsConfig={{
          onClickPrev: res.onClickPrev,
          onClickNext: res.onClickNext,
        }}
      />
    </Stack>
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const columns: ColumnProps<AirdropProjectDTOS>[] = useMemo(
    () =>
      isMobile
        ? [
            {
              dataKey: 'name',
              title: 'Project',
            },
            {
              dataKey: 'participateContent',
              title: 'Participants',
            },
            {
              dataKey: 'winnerCounts',
              title: 'Winners',
            },
          ]
        : [
            {
              dataKey: 'name',
              title: 'Project',
            },
            {
              dataKey: 'participateContent',
              title: 'Participants',
            },
            {
              dataKey: 'winnerCounts',
              title: 'Winners',
            },
            {
              dataKey: 'totalPrize',
              title: 'Rewards Pool',
            },
            {
              dataKey: 'participationStartTime',
              title: 'Started',
            },
            {
              dataKey: 'participationEndTime',
              title: 'Ended',
            },
            {
              dataKey: 'policies',
              title: 'Wallet Description',
            },
          ],
    [isMobile],
  );

  return (
    <Stack>
      {renderAirdropTable(ongoing, 'Ongoing')}
      {renderAirdropTable(upcoming, 'Upcoming')}
      {renderAirdropTable(ended, 'Ended')}
    </Stack>
  );
};

const Participated = () => {
  const { theme } = useTheme();
  const router = useRouter();
  const participatedData = useParticipatedList();
  const [_, setAirdropsDetail] = useAtom(airdropsDetail);
  const getRowStyle = (row: any) => {
    return {
      '&:hover': {
        bgcolor: '#000',
        cursor: 'pointer',
      },
    };
  };
  const renderAirdropTable = (res, title) => (
    <Stack>
      <Typography variant="h4" my={3}>
        {title} ({res.data?.airdropProjectDTOS?.length || 0})
      </Typography>
      <WrappedTable
        // getRowStyle={getRowStyle}
        // onRowClick={(item) => {
        //   setAirdropsDetail(item);
        //   router.push({ pathname: `/airdrops/${item.name}` });
        // }}
        loading={res.isLoading}
        sxs={{
          containerSX: {
            width: '100%',
            whiteSpace: 'nowrap',
          },
        }}
        columns={columns}
        data={res.data?.airdropProjectDTOS || []}
        anchoredButtonsConfig={{
          onClickPrev: res.onClickPrev,
          onClickNext: res.onClickNext,
        }}
      />
    </Stack>
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const columns: ColumnProps<AirdropProjectDTOS>[] = useMemo(
    () =>
      isMobile
        ? [
            {
              dataKey: 'name',
              title: 'Project',
            },
            {
              dataKey: 'participateContent',
              title: 'Participants',
            },
            {
              dataKey: 'winnerCounts',
              title: 'Winners',
            },
          ]
        : [
            {
              dataKey: 'name',
              title: 'Project',
            },
            {
              dataKey: 'participateContent',
              title: 'Participants',
            },
            {
              dataKey: 'winnerCounts',
              title: 'Winners',
            },
            {
              dataKey: 'totalPrize',
              title: 'Rewards Pool',
            },
            {
              dataKey: 'participationStartTime',
              title: 'Started',
            },
            {
              dataKey: 'participationEndTime',
              title: 'Ended',
            },
            {
              dataKey: 'policies',
              title: 'Wallet Description',
            },
          ],
    [isMobile],
  );

  return <Stack>{renderAirdropTable(participatedData, 'Participated')}</Stack>;
};
export { AllAirdrops, Participated };
